import { Brand } from '../config/brand.enum';

interface CheckoutHosts {
  /** Registrable domains the brand's checkout is served from. */
  domains: readonly string[];
  /** First host label of the brand's checkout (`checkout-insurance` in `checkout-insurance.aceable.com`). */
  checkout: string;
  /**
   * First label of the brand's own `clientHost`, which /reply falls back to for the payment-failed subscribe. Set only
   * on brands with a payment-failed list; the client contract test fails when a brand gains one without it.
   */
  home?: string;
}

/**
 * Where each brand's checkout lives, keyed by the `brandId` checkout posts: the Iterable brand. PrepAgent posts as `AA`
 * and runs on both AA domains. AARP has no lists, so it never reaches the link check and has no entry.
 * Prod hostnames are defined in infra (CDK, `aws/xcc-client-*`), not here. The client contract test checks this table
 * against each brand's `clientHost`, the stage checkouts in `apps/playwright-ecom/data/brandMatrix.ts` and the review
 * hosts in `xcc-cdk-reviewapp-deploy.yml`.
 */
const CHECKOUT_HOSTS: Readonly<Partial<Record<Brand, CheckoutHosts>>> = {
  [Brand.ACE]: { domains: ['aceable.com'], checkout: 'checkout' },
  [Brand.INS]: { domains: ['aceable.com'], checkout: 'checkout-insurance', home: 'insurance' },
  [Brand.MTG]: { domains: ['aceable.com'], checkout: 'checkout-mortgage', home: 'mortgage' },
  [Brand.HSP]: { domains: ['aceable.com'], checkout: 'checkout-hospitality' },
  [Brand.AA]: { domains: ['aceableagent.com', 'prepagent.com'], checkout: 'checkout', home: 'www' },
  [Brand.DEC]: { domains: ['driversed.com'], checkout: 'checkout' },
  [Brand.IDS]: { domains: ['idrivesafely.com'], checkout: 'checkout' },
};

/** Zones between the first label and the domain. Prod emails never link to dev, stage or review builds. */
const PROD_ZONES = ['', 'ecom-prod'];
const PRE_PROD_ZONES = [...PROD_ZONES, 'ecom-dev', 'ecom-stage'];
/**
 * The ticket a review app appends to its checkout label (`checkout-insurance-eps-5008.ecom-dev`). Same shape as the
 * slug `xcc-cdk-reviewapp-deploy.yml` cuts from the branch name, lowercased. Never an `-api-` host. The ticket can be
 * any project key, so ACE's pattern also matches an INS-shaped `checkout-insurance-12`; that's off prod only.
 */
const REVIEW_TICKET = /^-(?!api-)[a-z_]{3,15}-\d{2,30}$/;
/** Checkout routes are plain path segments, so a quote or bracket in the path is never a real checkout link. */
const CHECKOUT_PATH = /^\/[\w/-]*$/;

/**
 * Query params a checkout link drops, matched case-insensitively: attribution, tokens and one-visit state, so a caller
 * can't put their own affiliate, referral or campaign id into a branded email. Every other param stays, so the coupon
 * params a brand config names (`couponCode`, `vc`, `hc`, …) keep a recovered cart's price.
 */
export const CHECKOUT_LINK_DROPPED_PARAMS: readonly string[] = [
  // Ambassador, referral, campaign and affiliate attribution (TUNE's offer_* and device_* by exact name, so another
  // `offer_*` param a config names survives).
  'couponAmbassador',
  'couponAmbassadorRSA',
  'referral_id',
  'campaignid',
  'cjevent',
  'cjeventid',
  'offer_id',
  'offer_name',
  'device_brand',
  'device_name',
  'arpsup',
  // Ad-click ids that don't end in `clid` or `clkid`.
  'gbraid',
  'wbraid',
  'irclickid',
  'sscid',
  'li_fat_id',
  'epik',
  // Tokens and the non-prod API override.
  'token',
  'api_pr',
  // One /reply visit or one completed purchase.
  'receiptId',
  'redirect_status',
  'bnpl_failed',
  'purchaseSuccess',
];
/** Dropped families: `utm_source`, `aff_id`, `mbsy_source`, `payment_intent_client_secret`, `externalTransactionToken`. */
export const CHECKOUT_LINK_DROPPED_PREFIXES: readonly string[] = [
  'utm_',
  'aff_',
  'mbsy',
  'payment_intent',
  'externalTransaction',
];
/** Ad-click ids, any name ending in `clid` or `clkid`: `gclid`, `fbclid`, `dclid`, `ttclid`, `twclid`, `msclkid`. */
const CLICK_ID = /clk?id$/;

const DROPPED_NAMES = new Set(CHECKOUT_LINK_DROPPED_PARAMS.map((name) => name.toLowerCase()));
const DROPPED_PREFIXES = CHECKOUT_LINK_DROPPED_PREFIXES.map((prefix) => prefix.toLowerCase());

export const isDroppedLinkParam = (name: string): boolean => {
  const lower = name.toLowerCase();
  return (
    DROPPED_NAMES.has(lower) || DROPPED_PREFIXES.some((prefix) => lower.startsWith(prefix)) || CLICK_ID.test(lower)
  );
};

type HostKind = 'checkout' | 'home' | null;

const hostKind = (hostname: string, hosts: CheckoutHosts, zones: readonly string[]): HostKind => {
  for (const domain of hosts.domains) {
    if (!hostname.endsWith(`.${domain}`)) continue;
    const parts = hostname.slice(0, -domain.length - 1).split('.');
    if (parts.length > 2) return null;
    const [label, zone = ''] = parts;
    if (!zones.includes(zone)) return null;
    if (label === hosts.checkout) return 'checkout';
    const isReviewApp =
      zone === 'ecom-dev' &&
      label.startsWith(`${hosts.checkout}-`) &&
      REVIEW_TICKET.test(label.slice(hosts.checkout.length));
    if (isReviewApp) return 'checkout';
    return label === hosts.home ? 'home' : null;
  }
  return null;
};

const classify = (parsed: URL, hosts: CheckoutHosts, allowPreProd: boolean): HostKind => {
  if (parsed.hostname === 'localhost') {
    return allowPreProd && (parsed.protocol === 'http:' || parsed.protocol === 'https:') ? 'checkout' : null;
  }
  if (parsed.protocol !== 'https:' || parsed.port) return null;
  return hostKind(parsed.hostname, hosts, allowPreProd ? PRE_PROD_ZONES : PROD_ZONES);
};

/**
 * `url` in the form Iterable may email when it links to `brand`'s checkout, else null. A checkout link loses the
 * dropped params and its fragment. The brand's marketing home is allowed only as its bare `/`, the form
 * /reply falls back to. Dev, stage, review and `localhost` links are allowed only off prod.
 */
export const toAllowedCheckoutUrl = (brand: string, url: unknown, allowPreProd: boolean): string | null => {
  if (typeof url !== 'string') return null;
  // Own keys only: `__proto__` or `constructor` as a brand must not reach the prototype.
  const hosts = Object.prototype.hasOwnProperty.call(CHECKOUT_HOSTS, brand) ? CHECKOUT_HOSTS[brand as Brand] : null;
  if (!hosts) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.username || parsed.password) return null;

  const kind = classify(parsed, hosts, allowPreProd);
  if (kind === 'home') {
    return parsed.pathname === '/' && !parsed.search && !parsed.hash ? `${parsed.origin}/` : null;
  }
  if (kind !== 'checkout' || !CHECKOUT_PATH.test(parsed.pathname)) return null;

  const kept = new URLSearchParams();
  parsed.searchParams.forEach((value, key) => {
    if (!isDroppedLinkParam(key)) kept.append(key, value);
  });
  const search = kept.toString();
  return `${parsed.origin}${parsed.pathname}${search ? `?${search}` : ''}`;
};
